import { FetchAlertQuery } from '@/lib/api/monitoring/alert';
import { FetchFullStreamQuery } from '@/lib/api/monitoring/alert';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertLiveViewSidecarController } from './controller';
import HlsVideoElement from 'hls-video-element';
import { getTimeZoneName, useCurrentTime } from '@/lib/time';
import { extractVideoProcessors } from '@/lib/video-processor';
import { VideoProcessorSelector } from '../components/VideoProcessorSelector';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useCreateAlertNote } from '@/hooks/useCreateAlertNote';
import { VideoPlayerCompat } from '../AlertEventPlayback/components/VideoPlayer';
import { cn } from '@/lib/utils';

const TOOLBAR_HEIGHT = 16 * 4;

export const AlertLiveViewSidecar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialAlertId = searchParams.get('alertId') ?? undefined;
  const [alertId, setAlertId] = useState<string | undefined>(initialAlertId);
  const alertQuery = useQuery({
    ...FetchAlertQuery(alertId ?? ''),
    enabled: alertId != null,
  });
  const alert = alertQuery.data;
  const occurred = alert?.events?.[0]?.occurred;
  const [_selectedProcessorId, setSelectedProcessorId] = useState<string>();
  const processors = useMemo(() => {
    return extractVideoProcessors(alert?.events ?? []);
  }, [alert]);
  const [firstProcessor] = processors;
  const _selectedProcessor = useMemo(() => {
    return processors.find((p) => p.id === _selectedProcessorId);
  }, [processors, _selectedProcessorId]);
  const selectedProcessor = _selectedProcessor ?? firstProcessor;
  const canLoadStream =
    occurred != null &&
    alert?.companyId != null &&
    alert?.projectId != null &&
    selectedProcessor != null;
  const [hasBuffered, setHasBuffered] = useState(false);
  const streamQuery = useQuery({
    ...FetchFullStreamQuery({
      occurred: occurred ? new Date(occurred) : new Date(),
      companyId: alert?.companyId ?? '',
      projectId: alert?.projectId ?? '',
      videoProcessorIdentifier: selectedProcessor?.identifier ?? '',
    }),
    enabled: canLoadStream,
    staleTime: 60 * 1000,
  });
  const stream = streamQuery.data;
  const streamUrl = stream?.liveUrl;
  const streamUrlType = stream?.liveUrlType;
  const videoRef = useRef<HTMLVideoElement | HlsVideoElement>(null);
  const currentTime = useCurrentTime();
  const liveViewStartRef = useRef<number | null>(null);
  const createNoteMutation = useCreateAlertNote({
    companyId: alert?.companyId ?? '',
    projectId: alert?.projectId ?? '',
    alertId: alert?.id ?? '',
  });

  const _setAlertId = useCallback(
    (alertId: string | undefined) => {
      setAlertId(alertId);
      setHasBuffered(false);
    },
    [setAlertId, setHasBuffered]
  );

  useEffect(() => {
    if (initialAlertId == null) return;
    if (initialAlertId != alertId) {
      _setAlertId(initialAlertId);
      setSearchParams((params) => {
        params.delete('alertId');
        return params;
      });
    }
  }, [initialAlertId, setSearchParams, _setAlertId]);

  useEffect(() => {
    const unsub = AlertLiveViewSidecarController.registerHandlerChild((event) => {
      switch (event.type) {
        case 'alert-changed': {
          _setAlertId(event.alertId);
          break;
        }
        case 'alert-closed': {
          _setAlertId(undefined);
          break;
        }
      }
    });

    return unsub;
  }, [_setAlertId]);

  const onCloseWindow = () => {
    window.close();
  };

  // Set window size according to video
  const onVideoLoad = () => {
    setHasBuffered(true);
    if (streamUrl) {
      liveViewStartRef.current = Date.now();
    }
    const video = videoRef.current;
    if (!video) return;

    const videoWidth = video.videoWidth || 1280; // Fallback dimensions
    const videoHeight = video.videoHeight || 720;
    const aspectRatio = videoWidth / videoHeight;

    // Account for window chrome (adjust these values as needed)
    const chromeWidth = 16; // padding, borders, etc.
    const chromeHeight = 80; // title bar, address bar, etc.

    const maxWidth = 1200;
    const newWidth = Math.min(videoWidth, maxWidth) + chromeWidth;
    const newHeight = (newWidth - chromeWidth) / aspectRatio + chromeHeight + TOOLBAR_HEIGHT;

    try {
      window.resizeTo(Math.ceil(newWidth), Math.ceil(newHeight));
    } catch (e) {
      console.warn('Could not resize window:', e);
    }
  };

  useEffect(() => {
    return () => {
      if (liveViewStartRef.current) {
        const seconds = Math.round((Date.now() - liveViewStartRef.current) / 1000);
        const cameraName = selectedProcessor?.name ?? 'Unknown Camera';
        createNoteMutation.mutate({
          note: `Viewed live feed of ${cameraName} for ${seconds}s`,
        });
        liveViewStartRef.current = null;
      }
    };
  }, [selectedProcessor?.id, streamUrl]);

  if (alertId == null) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-white">No alert selected</h2>
        <h5 className="text-white">Select an alert to view the live stream</h5>
      </div>
    );
  }

  if (alertQuery.isLoading) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center">
        <h2 className="text-white">Loading stream</h2>
      </div>
    );
  }

  if (alert == null) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center">
        <h2 className="text-white">Failed to load alert</h2>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen flex-col bg-[#222]">
      <div
        className="flex items-center justify-between overflow-hidden px-4"
        style={{ height: TOOLBAR_HEIGHT }}
      >
        <div className="flex items-center gap-x-8">
          <p className="text-xl font-bold text-white">{alert.alertId}</p>
          <VideoProcessorSelector
            processors={processors}
            selectedProcessorId={selectedProcessor?.id}
            onSelect={setSelectedProcessorId}
          />
        </div>

        <div className="flex items-center gap-x-4">
          <div className="flex flex-col gap-y-1 text-right text-xs">
            <p className="font-semibold">{alert.project?.name}</p>
            <p>{alert.companyName}</p>
          </div>

          {alert.project && (
            <div className="border-l border-l-white/50 pl-4">
              <p className="text-2xl font-bold leading-tight text-white">
                {currentTime.toLocaleString('en-US', {
                  timeZone: alert.project.timeZone,
                  hour: 'numeric',
                  minute: 'numeric',
                })}
              </p>
              <p className="text-center text-xs font-bold uppercase opacity-70">
                {currentTime.toLocaleString('en-US', {
                  timeZone: alert.project.timeZone,
                  day: '2-digit',
                  month: 'short',
                })}
                <span className="px-1">&bull;</span>
                <span className="text-stone-300">{getTimeZoneName(alert.project.timeZone)}</span>
              </p>
            </div>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="ml-2 border border-white/30"
            onClick={onCloseWindow}
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center p-2">
        {streamQuery.isLoading || !hasBuffered ? (
          <div className="absolute inset-0 flex h-full w-full items-center justify-center gap-4">
            <p className="text-lg font-bold text-gray-500">Connecting To Camera</p>
            <Spinner size={'large'} className="text-gray-500" />
          </div>
        ) : null}

        {streamQuery.isLoading ? null : (
          <>
            {streamUrl && streamUrlType ? (
              <>
                <LiveBadge />

                <VideoPlayerCompat
                  url={streamUrl}
                  urlType={streamUrlType}
                  className="rounded-md border border-white/40"
                  ref={videoRef}
                  onLoad={onVideoLoad}
                  controls={false}
                />
              </>
            ) : (
              <div className="text-white">No stream available</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const LiveBadge = (props: ComponentProps<'div'>) => {
  return (
    <div
      style={{
        animationDuration: '3s',
      }}
      {...props}
      className={cn(
        'absolute w-max right-4 top-4 flex animate-pulse items-center gap-2 rounded-md bg-stone-800/80 px-1 text-rose-400',
        props.className
      )}
    >
      <div className="h-2 w-2 rounded-full bg-rose-400" />
      <p className="text-xs font-bold uppercase">Live</p>
    </div>
  );
};
